/**
 * Admin Portal: Language control — enable/disable languages on the platform.
 * Only content in enabled languages is displayed (e.g. Streaming catalogue). Default: English only.
 * Admin-only. Pattern: Channel Control (listing_channel_display).
 */
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ENABLED_LANGUAGES_QUERY_KEY } from '@/hooks/useEnabledLanguages'

const LANGUAGE_LIST_QUERY_KEY = 'platform-enabled-languages-list'

interface PlatformEnabledLanguage {
  id: string
  language_code: string
  display_name: string
  is_enabled: boolean
  display_order: number
  created_at: string
  updated_at: string
}

export default function AdminLanguageControlPage() {
  const { user } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()

  const { data: languageList = [], isLoading: listLoading, error: listError } = useQuery({
    queryKey: [LANGUAGE_LIST_QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_enabled_languages')
        .select('id, language_code, display_name, is_enabled, display_order, created_at, updated_at')
        .order('display_order', { ascending: true })
        .order('display_name')
      if (error) throw error
      return (data || []) as PlatformEnabledLanguage[]
    },
    enabled: !!user && isAdmin,
  })

  const updateLanguageMutation = useMutation({
    mutationFn: async ({ id, is_enabled }: { id: string; is_enabled: boolean }) => {
      const { error } = await supabase
        .from('platform_enabled_languages')
        .update({ is_enabled })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LANGUAGE_LIST_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ENABLED_LANGUAGES_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
    },
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ is_enabled, ids }: { is_enabled: boolean; ids: string[] }) => {
      const BATCH = 100
      for (let i = 0; i < ids.length; i += BATCH) {
        const chunk = ids.slice(i, i + BATCH)
        const { error } = await supabase
          .from('platform_enabled_languages')
          .update({ is_enabled })
          .in('id', chunk)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LANGUAGE_LIST_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ENABLED_LANGUAGES_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
    },
  })

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1
        className="text-3xl font-bold mb-6"
        style={{ color: 'var(--text-primary)' }}
      >
        Language control
      </h1>

      <section
        className="rounded-lg p-6 border-2"
        style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
        aria-labelledby="language-control-heading"
      >
        <h2 id="language-control-heading" className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Enable or disable languages on the platform
        </h2>
        <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Only programmes in enabled languages are shown (e.g. on the Streaming page). Toggle each language to show or hide content in that language. Default: only English enabled.
        </p>
        {listLoading ? (
          <p style={{ color: 'var(--text-secondary)' }}>Loading languages…</p>
        ) : listError ? (
          <p style={{ color: 'var(--color-error)' }} role="alert">
            {listError instanceof Error ? listError.message : 'Failed to load language list.'}
          </p>
        ) : languageList.length === 0 ? (
          <p style={{ color: 'var(--text-primary)' }}>
            No languages configured. Run migration 080 (platform_enabled_languages) to seed the list.
          </p>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => bulkUpdateMutation.mutate({ is_enabled: true, ids: languageList.map((l) => l.id) })}
                disabled={bulkUpdateMutation.isPending || updateLanguageMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded border"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                {bulkUpdateMutation.isPending ? 'Updating…' : 'Enable all'}
              </button>
              <button
                type="button"
                onClick={() => bulkUpdateMutation.mutate({ is_enabled: false, ids: languageList.map((l) => l.id) })}
                disabled={bulkUpdateMutation.isPending || updateLanguageMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded border"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                {bulkUpdateMutation.isPending ? 'Updating…' : 'Disable all'}
              </button>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Only enabled languages are shown to users (Streaming and future TV language filter).
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                    <th className="text-left py-2 pr-4">Language</th>
                    <th className="text-left py-2 pr-4" style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Code</th>
                    <th className="text-left py-2 pr-4">Enabled</th>
                  </tr>
                </thead>
                <tbody>
                  {languageList.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                      <td className="py-2 pr-4">{row.display_name}</td>
                      <td className="py-2 pr-4 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{row.language_code}</td>
                      <td className="py-2 pr-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={row.is_enabled}
                            onChange={(e) => updateLanguageMutation.mutate({ id: row.id, is_enabled: e.target.checked })}
                            disabled={updateLanguageMutation.isPending || bulkUpdateMutation.isPending}
                            className="rounded"
                            aria-label={`${row.is_enabled ? 'Disable' : 'Enable'} ${row.display_name}`}
                          />
                          <span style={{ color: 'var(--text-secondary)' }}>{row.is_enabled ? 'Enabled' : 'Disabled'}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
